import { Divider } from "@mui/material";
import {
	CompanyTestResultFieldType,
	ExpectationFieldType,
	Severity,
	WorkflowType,
} from "node-library/dist/constants";
import {
	FrontendBatchType,
	FrontendClient,
	FrontendCompanyResultFieldHierarchyNodeMappingPackage,
	FrontendCompanyTestCategory,
	FrontendCompanyTestResultField,
	FrontendCompanyTestResultUnits,
	FrontendCompanyWithAdditionalInfomation,
	FrontendOrganization,
	SysAdminCompanyTestingWorkflowWithBatchTypeIds,
} from "node-library/dist/portal";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonTextLoadingAnimation } from "../../../../common-components/animation/ButtonLoadingAnimation";
import { GreenButton, TextButton } from "../../../../common-components/button";
import CountryProvinceSelector from "../../../../common-components/countryAndProvinceSelector";
import {
	CommonSwitch,
	CustomCheckbox,
	CustomDropdown,
	CustomTextInput,
} from "../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../common-components/modals/modal";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { validateEmail } from "../../../../helpers/inputHelper";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";
import { DeleteIcon } from "../../../../common-components/svgIcons";
import AddIcon from "@mui/icons-material/Add";
import { createUUID } from "node-library/dist/helpers/request_helper";
import CommonAutoCompleteDropdown from "../../../../common-components/commonAutoCompleteDropdown";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	companyBatchType: Array<FrontendBatchType>;
	addOrEditWorkflow: (
		workflow: SysAdminCompanyTestingWorkflowWithBatchTypeIds,
		isEdit: boolean,
	) => void;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	loading: boolean;

	workflow?: SysAdminCompanyTestingWorkflowWithBatchTypeIds;
}

export default function AddOrWditWorkflowBasicInfoModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		open,
		setOpen,
		addOrEditWorkflow,
		companyBatchType,
		workflow,
		loading,
	} = props;

	const [workflowName, setWorkflowName] = useState<string>(
		workflow?.workflowName || "",
	);
	const [workflowType, setWorkflowType] = useState<WorkflowType>(
		workflow?.workflowType || WorkflowType.INDIVIDUAL,
	);

	const [batchTypeIds, setBatchTypeIds] = useState<Array<string>>(
		workflow?.batchTypeIds || [],
	);

	const selectedBatchTypes: Array<FrontendBatchType> = useMemo(() => {
		const filtered = companyBatchType.filter((batchType) =>
			batchTypeIds.includes(batchType.batchTypeId),
		);

		// Sort by Category Name (or ID) for a cleaner UI
		return filtered.sort((a, b) => {
			return a.batchTypeId.localeCompare(b.batchTypeId);
		});
	}, [companyBatchType]);

	//validations
	const [workflowNameErrorMsg, setWorkflowNameErrorMsg] = useState<string>("");

	function handleSubmit() {
		if (!workflowName || workflowName.trim().length == 0) {
			setWorkflowNameErrorMsg(translation("MissingInformation"));
			return;
		}
		setWorkflowNameErrorMsg("");
		let workflowToSubmit: SysAdminCompanyTestingWorkflowWithBatchTypeIds;

		workflowToSubmit = {
			workflowId: workflow?.workflowId || "",
			companyId: "",
			workflowName,
			workflowType,
			batchTypeIds: batchTypeIds,
		};

		addOrEditWorkflow(workflowToSubmit, workflow ? true : false);
	}

	useEffect(() => {
		if (open) {
			setWorkflowNameErrorMsg("");

			setWorkflowName(workflow?.workflowName || "");
			setWorkflowType(workflow?.workflowType || WorkflowType.INDIVIDUAL);

			setBatchTypeIds(workflow?.batchTypeIds || []);
		}
	}, [open]);

	const fieldNameInput: React.ReactNode = (
		<CustomTextInput
			id="result-fieldname-input"
			key="result-fieldname-input"
			value={workflowName}
			setValue={setWorkflowName}
			label={translation("WorkflowName")}
			type="text"
			required
			errorMsg={workflowNameErrorMsg}
			setErrorMsg={setWorkflowNameErrorMsg}
		></CustomTextInput>
	);

	const fieldTypeInput: React.ReactNode = (
		<CustomDropdown
			id={"field-type-name"}
			label={translation("Type")}
			options={Object.values(WorkflowType).map((value) => {
				return { value: value, title: translation(value) };
			})}
			value={workflowType}
			setValue={setWorkflowType}
		></CustomDropdown>
	);

	const removeBatchType = (index: number) => {
		const updatedIds = batchTypeIds.filter((_, i) => i !== index);
		setBatchTypeIds(updatedIds);
	};

	const newBatchType = () => {
		// Find the first package that hasn't been used yet
		const nextAvailable = companyBatchType.find(
			(p) => !batchTypeIds.includes(p.batchTypeId),
		);

		if (nextAvailable) {
			setBatchTypeIds([...batchTypeIds, nextAvailable.batchTypeId]);
		} else {
			// Use your snackbar to warn the user if all packages are already assigned
			openSnackbar(translation("AllAvailableBatchTypeAssigned"), Severity.WARN);
		}
	};

	const batchTypeItems: Array<React.ReactNode> = batchTypeIds.map(
		(selectedId, index) => {
			// 1. Find the full object for the currently selected ID
			const selectedBatchType = companyBatchType.find(
				(p) => p.batchTypeId === selectedId,
			);

			// 2. Filter available options so a package can't be selected twice,
			// but include the one currently selected for this specific row.
			const availableOptions = companyBatchType.filter(
				(p) =>
					!batchTypeIds.includes(p.batchTypeId) || p.batchTypeId === selectedId,
			);

			return (
				<div
					key={`node-pkg-${index}`}
					style={{
						display: "flex",
						gap: "10px",
						alignItems: "center",
						marginBottom: "10px",
					}}
				>
					<div style={{ width: "100%" }}>
						<CommonAutoCompleteDropdown
							id={`node-package-autocomplete-${index}`}
							label={translation("BatchType")}
							options={availableOptions}
							// groupBy={(
							// 	batchType: FrontendBatchType,
							// ) => {
							// 	const category = company.companyTestCategories.find(
							// 		(c: FrontendCompanyTestCategory) =>
							// 			c.testCategoryId === batchType.testCategoryId,
							// 	);
							// 	return category
							// 		? category.testCategoryName
							// 		: translation("Other");
							// }}
							getOptionLabel={(batchType: FrontendBatchType) =>
								batchType.batchName
							}
							value={selectedBatchType}
							setValue={(newValue: FrontendBatchType | null) => {
								if (newValue) {
									const updatedIds = [...batchTypeIds];
									updatedIds[index] = newValue.batchTypeId;
									setBatchTypeIds(updatedIds);
								}
							}}
						/>
					</div>
					<div
						style={{ cursor: "pointer", marginTop: "15px" }} // Added margin to align with autocomplete label height
						onClick={() => removeBatchType(index)}
					>
						<DeleteIcon />
					</div>
				</div>
			);
		},
	);
	const batchTypeBox: React.ReactNode = (
		<>
			<div
				className={`typography-body-02`}
				style={{
					display: "flex",
					flexWrap: "wrap",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<div>{translation("BatchTypes")}</div>
				<div className="submission-confirm-info-row-info">
					<TextButton
						id={"add-test-report-email-button"}
						onClick={() => {
							newBatchType();
						}}
						size="xsmall"
					>
						<AddIcon style={{ fontSize: 20 }} />
						<span className="typography-body-02">
							{translation("BatchType")}
						</span>
					</TextButton>
				</div>
			</div>
			<div>{batchTypeItems}</div>
		</>
	);

	const modalContent: React.ReactNode = (
		<div>
			{fieldNameInput}
			{fieldTypeInput}
			{batchTypeBox}
		</div>
	);

	const workflowtModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={
				props.workflow
					? translation("EditWorkflow")
					: translation("CreateWorkflow")
			}
			// headerDescription={translation("ConfirmApprovalDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<GreenButton
					key="submit-client-change-button"
					id="submit-client-change-button"
					children={
						loading ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : props.workflow ? (
							translation("Edit")
						) : (
							translation("Create")
						)
					}
					disabled={loading}
					onClick={handleSubmit}
				></GreenButton>,
			]}
		>
			{modalContent}
		</ModalWithButtons>
	);
	return workflowtModal;
}
